import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  reportsApi,
  type PeriodQuery,
  type SalesReport,
  type ReceivablesReport,
  type ProductionReport,
  type Coverage,
} from "../../api/reports";
import type { ApiError } from "../../api/client";
import { ExportButton } from "../../components/ExportButton";
import { SendReportModal } from "../../components/reports/SendReportModal";
import { ProizvodiTabela } from "../../components/reports/ProizvodiTabela";
import { Select } from "../../components/Select";
import { KpiTile } from "../../components/reports/KpiTile";
import { TableSkeleton } from "../../components/Loading";
import { ReportPeriodPicker, periodQuery, type PeriodPickerValue } from "../../components/reports/ReportPeriodPicker";
import { formatDate, formatNumber } from "../../lib/format";
import { useUrlParam } from "../../lib/useUrlParam";
import { useUrlZapis } from "../../lib/useUrlZapis";
import { useAuth } from "../../auth/AuthContext";
import KomercijalistiTab from "./KomercijalistiTab";
import PotrazivanjaTab from "./PotrazivanjaTab";
import ProizvodnjaTab from "./ProizvodnjaTab";
import { th } from "./stil";
import ZemljeTab from "./ZemljeTab";
import SpornoIBeleske from "./SpornoIBeleske";
import { useChartPalette } from "../../theme/useChartPalette";

/**
 * Mesečni izveštaj komercijale, prodajne strane.
 *
 * Štampani izveštaj je devet tabela jedna pod drugom. Ovde su dve stvari drugačije, i obe namerno:
 * tabele su razdvojene na tabove jer se čitaju odvojeno, a uz svaku stoji slika koja kaže ono što
 * tabela ne ume — zašto se marža pomerila.
 *
 * Dve tvrdnje koje ekran mora da drži:
 * 1. Reč „indeks" na proizvodima znači odnos prema prošloj godini, a na zemljama **udeo u
 *    ukupnom** (kolone se sabiraju na 100). To su dve kolone, nikad jedna.
 * 2. Ulje i sačma se čitaju **zajedno**: minus sačme je druga strana plusa ulja iz iste
 *    ekstrakcije, pa slika koja sačmu pokaže samu laže o poslu.
 */

type Tab = "products" | "countries" | "salespeople" | "production" | "receivables";
const TABOVI: readonly Tab[] = ["products", "countries", "salespeople", "production", "receivables"];


/**
 * Sme li se iz pokrivenosti čitati rast ili pad — jedno mesto za ceo ekran.
 *
 * Isto pravilo koje servis primenjuje na indeks (`Merilo`) i koje PDF čita kroz `index`. Ekran
 * promenu računa sam, pa pravilo mora da postoji i ovde — ali na JEDNOM mestu, jer dve kopije
 * istog pravila razilaze se pri prvoj izmeni, a razlika bi bila nevidljiva: isti ekran, ista
 * brojka, drugi zaključak.
 *
 * Nepoznata pokrivenost se ponaša kao potpuna: dok nam niko nije rekao dokle smo stigli, nemamo
 * osnov ni da tvrdimo ni da ćutimo.
 */
function pokrivenostPodnosiTumacenje(c: SalesReport["period"]["coverage"] | undefined): boolean {
  return c == null || (c.current === "FULL" && c.previous === "FULL");
}

export default function ManagementReportPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "sr";
  const palette = useChartPalette();

  const [tab, setTab] = useUrlParam<Tab>("tab", "products", TABOVI);
  // Period i zemlja žive u adresi, kroz jedan upisivač: menjaju se u grupi i svaki od njih
  // obesmišljava ostale, a dva odvojena upisa u istom potezu bi se pregazila.
  const [izbor, izmeni] = useUrlZapis(
    ["kind", "year", "month", "quarter", "asOf", "from", "to", "country", "sporna"] as const,
  );

  const danas = useMemo(() => new Date(), []);
  const period: PeriodPickerValue = useMemo(() => {
    // Podrazumevano: prošli mesec. Izveštaj se pravi za mesec koji je zatvoren, ne za tekući.
    const prosli = new Date(danas.getFullYear(), danas.getMonth() - 1, 1);
    return {
      kind: (izbor.kind as PeriodPickerValue["kind"]) || "MONTH",
      year: Number(izbor.year) || prosli.getFullYear(),
      month: Number(izbor.month) || prosli.getMonth() + 1,
      quarter: Number(izbor.quarter) || Math.floor(prosli.getMonth() / 3) + 1,
      asOf: izbor.asOf || danas.toISOString().slice(0, 10),
      from: izbor.from || new Date(danas.getFullYear(), 0, 1).toISOString().slice(0, 10),
      to: izbor.to || danas.toISOString().slice(0, 10),
    };
  }, [izbor, danas]);

  const [podaci, setPodaci] = useState<SalesReport | null>(null);
  const [greska, setGreska] = useState<string | null>(null);
  const [ucitava, setUcitava] = useState(false);

  // Potraživanja su drugi upit i čitaju se samo kad se njihov tab otvori: odeljak je težak i nema
  // razloga da se vuče uz prodaju koju niko nije ni zatražio.
  const [potrazivanja, setPotrazivanja] = useState<ReceivablesReport | null>(null);
  const [greskaP, setGreskaP] = useState<string | null>(null);
  const [ucitavaP, setUcitavaP] = useState(false);
  const jePotrazivanja = tab === "receivables";

  // Proizvodnja je treci upit i cita se samo kad se njen tab otvori.
  const [proizvodnja, setProizvodnja] = useState<ProductionReport | null>(null);
  const [greskaR, setGreskaR] = useState<string | null>(null);
  const [ucitavaR, setUcitavaR] = useState(false);
  const jeProizvodnja = tab === "production";
  const { hasPermission } = useAuth();
  const smeDaUpise = hasPermission("management_report:mark");

  // Period, zemlja i sporna potraživanja, jednom: iz istog upita se čita i izvozi, pa se ne mogu razići.
  const upitIzvestaja = useMemo(
    () => ({ ...periodQuery(period, izbor.country), ...(izbor.sporna === "1" ? { sporna: true } : {}) }),
    [period, izbor.country, izbor.sporna],
  );
  const kljuc = JSON.stringify(upitIzvestaja);
  /** Isti upit kojim se čita, pretvoren u adresu izvoza — jedan izvor, pa se ne mogu razići. */
  const izvozniUpit = useMemo(() => {
    const q: Record<string, unknown> = { ...upitIzvestaja };
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v != null && v !== "") p.set(k, String(v));
    return p.toString();
  }, [upitIzvestaja]);
  /** Natpis perioda za prozor slanja: čovek pre slanja vidi šta tačno šalje. */
  /**
   * Da li se iz ovog perioda sme čitati rast ili pad.
   *
   * Netačno kad period prelazi presek izvora: septembar 2026 nosi 22 dana a poredi se sa punim
   * mesecom. Nepoznata pokrivenost se ponaša kao potpuna — dok nam niko nije rekao dokle smo
   * stigli, nemamo osnov ni da tvrdimo ni da ćutimo.
   *
   * NEPOTPUN JE BILO KOJI OD DVA PERIODA. Ovde je do 13.10.2026 stajalo samo `current`, pa je
   * nepotpun PRETHODNI prolazio — a on daje najveće i najuverljivije brojke, jer je imenilac
   * kraći: za jedan CUSTOM period 2,59 na količini i 4,48 na RUC-u. Ekran promenu računa SAM i
   * nikad ne čita indeks koji server pošalje, pa serverska zaštita sama po sebi do njega ne
   * stiže — pravilo mora da stoji i ovde.
   */
  const tumaciPeriod = pokrivenostPodnosiTumacenje(podaci?.period.coverage);

  const opisPerioda = podaci
    ? `${formatDate(podaci.period.from, locale)} – ${formatDate(podaci.period.to, locale)}`
    : `${period.year}`;
  const ucitaj = useCallback(async () => {
    setUcitava(true);
    try {
      const r = await reportsApi.sales(JSON.parse(kljuc));
      setPodaci(r);
      // Greška se briše pri sledećem uspešnom čitanju, ne ostaje da visi nad tačnim podacima.
      setGreska(null);
    } catch (e) {
      setGreska((e as ApiError).message);
    } finally {
      setUcitava(false);
    }
  }, [kljuc]);

  useEffect(() => {
    void ucitaj();
  }, [ucitaj]);

  const ucitajPotrazivanja = useCallback(async () => {
    setUcitavaP(true);
    try {
      const r = await reportsApi.receivables(JSON.parse(kljuc));
      setPotrazivanja(r);
      setGreskaP(null);
    } catch (e) {
      setGreskaP((e as ApiError).message);
    } finally {
      setUcitavaP(false);
    }
  }, [kljuc]);

  useEffect(() => {
    if (jePotrazivanja) void ucitajPotrazivanja();
  }, [jePotrazivanja, ucitajPotrazivanja]);

  const ucitajProizvodnju = useCallback(async () => {
    setUcitavaR(true);
    try {
      const r = await reportsApi.production(JSON.parse(kljuc));
      setProizvodnja(r);
      setGreskaR(null);
    } catch (e) {
      setGreskaR((e as ApiError).message);
    } finally {
      setUcitavaR(false);
    }
  }, [kljuc]);

  useEffect(() => {
    if (jeProizvodnja) void ucitajProizvodnju();
  }, [jeProizvodnja, ucitajProizvodnju]);

  const godine = useMemo(() => {
    const sada = danas.getFullYear();
    const out: number[] = [];
    for (let g = sada; g >= sada - 6; g--) out.push(g);
    return out;
  }, [danas]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head" style={{ alignItems: "flex-start" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>
            {t("report.title", "Izveštaj komercijale")}
          </h1>
          <div className="sub">
            {podaci
              ? t("report.subtitle", {
                  od: formatDate(podaci.period.from, locale),
                  do: formatDate(podaci.period.to, locale),
                  defaultValue: "{{od}} – {{do}}, uz isti period prošle godine",
                })
              : t("report.subtitleEmpty", "Prodaja po grupama proizvoda i po zemljama")}
          </div>
        </div>
        {/* Izvoz nosi ISTE filtere koje nosi tabela, i prati otvoren tab: fajl koji pokriva drugi
            period — ili drugi odeljak — od ekrana sa kog je preuzet je gori od izvoza kog nema.
            PDF je ceo izveštaj u jednom dokumentu i ne prati tab: to je fajl koji ide menadžmentu,
            pa nosi sve odeljke redom kojim se štampani izveštaj i čita. */}
        {/* Dugmad stoje u JEDNOJ grupi. `.page-head` razmiče svoju decu na krajeve, pa su dva
            zasebna dugmeta ostavljala PDF nasred zaglavlja a Excel uz desnu ivicu. */}
        <div className="page-actions">
          {(podaci || potrazivanja) && (
            <ExportButton
              path={`/exports/management-report.pdf?${izvozniUpit}`}
              label={t("report.exportPdf", "Preuzmi PDF")}
              filename={`izvestaj-komercijale-${period.year}-${String(period.month).padStart(2, "0")}.pdf`}
            />
          )}
          {/* Excel PRATI TAB, i svaki tab ima svoj. Dugme na tabu proizvodnje je do 13.10.2026
              preuzimalo PRODAJU: čovek koji gleda tonaže pogona dobijao je fajl o kupcima, a
              pogrešan fajl je gori od fajla kog nema, jer se prosleđuje. */}
          {jePotrazivanja && potrazivanja && (
            <ExportButton
              path={`/exports/receivables-report.xlsx?${izvozniUpit}`}
              label={t("report.export", "Preuzmi Excel")}
              filename={`potrazivanja-${period.year}-${String(period.month).padStart(2, "0")}.xlsx`}
            />
          )}
          {jeProizvodnja && proizvodnja && (
            <ExportButton
              path={`/exports/production-report.xlsx?${izvozniUpit}`}
              label={t("report.export", "Preuzmi Excel")}
              filename={`proizvodnja-${proizvodnja.from}_${proizvodnja.to}.xlsx`}
            />
          )}
          {!jePotrazivanja && !jeProizvodnja && podaci && (
            <ExportButton
              path={`/exports/sales-report.xlsx?${izvozniUpit}`}
              label={t("report.export", "Preuzmi Excel")}
              filename={`izvestaj-komercijale-${podaci.period.from}_${podaci.period.to}.xlsx`}
            />
          )}
          {/* Slanje stoji uz preuzimanje, ali ga vidi samo ko sme da ga uradi: fajl nosi nabavnu
              cenu, maržu po kupcu i stanje potraživanja, pa je kapija WRITE nad odeljkom, ista
              ona koju drže četiri role koje izveštaj i sastavljaju. */}
          {(podaci || potrazivanja) && smeDaUpise && (
            <SendReportModal upit={izvozniUpit} period={opisPerioda} />
          )}
        </div>
      </div>

      <section className="card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
        <ReportPeriodPicker
          value={period}
          years={godine}
          disabled={ucitava}
          onChange={(izmene) => {
            const kao: Record<string, string> = {};
            for (const [k, v] of Object.entries(izmene)) kao[k] = String(v);
            izmeni(kao);
          }}
        />
        {/* Filter zemlje pripada prodajnom ledgeru; nad knjigom ne radi ništa, pa se i ne nudi.
            NA PROIZVODNJI SE NUDI, jer podela brašna se računa iz ledgera i nosi zemlju. Do
            13.10.2026 je ovde bio sakriven a zemlja je ostajala u adresi: čovek je sa prodaje
            prelazio na proizvodnju i nije imao čime da je promeni ni skloni. Koja tabela zemlju
            poznaje a koja ne, piše uz same tabele. */}
        {/* Sporna potraživanja: podrazumevano ne ulaze u potraživanja ni na ekranu ni u izvozu (PDF, Excel, mejl). */}
        {jePotrazivanja && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 260 }}>
            <span style={th}>{t("disputes.filter.label", "Disputed receivables")}</span>
            <Select
              value={izbor.sporna === "1" ? "1" : ""}
              options={[
                { value: "", label: t("disputes.filter.hide", "Don't show disputed") },
                { value: "1", label: t("disputes.filter.show", "Show disputed") },
              ]}
              onChange={(v) => izmeni({ sporna: v })}
              disabled={ucitavaP}
              ariaLabel={t("disputes.filter.label", "Disputed receivables")}
            />
          </div>
        )}
        {!jePotrazivanja && podaci && podaci.availableCountries.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 260 }}>
            <span style={th}>{t("report.country", "Zemlja")}</span>
            <Select
              value={izbor.country}
              options={[
                { value: "", label: t("report.allCountries", "Sve zemlje") },
                ...podaci.availableCountries.map((z) => ({ value: z, label: z })),
              ]}
              onChange={(country) => izmeni({ country })}
              disabled={ucitava}
              ariaLabel={t("report.country", "Zemlja")}
            />
          </div>
        )}
      </section>

      {/* ŠTA PORTAL ZAPRAVO DRŽI. Bez ove rečenice brojka se ne može odbraniti: kopija izvora seže
          do 22.09.2026, pa svaki period koji je u toku izgleda manji nego što je bio, a za 2023.
          prethodne godine u ledgeru nema uopšte. Pokrivenost `null` znači NEPOZNATO — tada se ne
          piše ništa, jer tvrdnja o potpunosti koju nam niko nije dao nije naša da je damo. */}
      {/* ISTA OGRADA NA SVAKOM TABU KOJI JE IMA. Dok je stajala samo u prodajnoj grani, proizvodni
          tab je o istom nepunom periodu ćutao — a podela brašna je prodajna mera i nosi iste dve
          godine. Obe strane paketa sada čitaju svoju pokrivenost, i proizvodna svoju. */}
      {!jePotrazivanja && (jeProizvodnja ? proizvodnja : podaci) && (
        <Pokrivenost
          coverage={jeProizvodnja ? (proizvodnja?.coverage ?? null) : (podaci?.period.coverage ?? null)}
          prevFrom={jeProizvodnja ? proizvodnja?.prevFrom : podaci?.period.prevFrom}
          prevTo={jeProizvodnja ? proizvodnja?.prevTo : podaci?.period.prevTo}
          locale={locale}
        />
      )}

      {!jePotrazivanja && !jeProizvodnja && podaci && !podaci.period.frozen && (
        /* Zamrznut mesec je konačan. Dok nije, ERP još može da prepiše nabavnu cenu unazad i marža
           se pomeri pod izveštajem — izmereno 4,87 % na mesecu koji je tek prošao. Brojka koja se
           još može promeniti mora to da kaže, inače se ne može odbraniti. */
        <div
          className="card"
          style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink-2)", borderLeft: "3px solid var(--warn)" }}
        >
          {t(
            "report.provisional",
            "Period nije zamrznut, pa se marža još može pomeriti: ERP prepisuje nabavnu cenu i unazad.",
          )}
        </div>
      )}
      {!jePotrazivanja && !jeProizvodnja && podaci?.period.frozen && (
        <div
          className="card"
          style={{ padding: "10px 14px", fontSize: 13, color: "var(--ink-2)", borderLeft: "3px solid var(--ok)" }}
        >
          {t("report.frozen", {
            datum: formatDate(podaci.period.frozenAt, locale),
            defaultValue: "Period je zamrznut {{datum}} i brojke su konačne.",
          })}
        </div>
      )}

      {!jePotrazivanja && !jeProizvodnja && greska && <div className="card error">{greska}</div>}
      {jeProizvodnja && greskaR && <div className="card error">{greskaR}</div>}
      {jePotrazivanja && greskaP && <div className="card error">{greskaP}</div>}

      {/* Nepotpun period ne dobija tumačenje: brojke se pokazuju, rast ili pad se ne čita.
          Oznaka u rečenici i zelena strelica uz nju su ista tvrdnja dva puta, a boja pobeđuje. */}
      {!jePotrazivanja && !jeProizvodnja && podaci && (
        <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginBottom: 0 }}>
          <KpiTile
            label={t("report.kpi.quantity", "Prodata količina")}
            value={podaci.totals.quantity.current}
            previous={podaci.totals.quantity.previous}
            unit="kg"
            locale={locale}
            tumaci={tumaciPeriod}
          />
          <KpiTile
            label={t("report.kpi.value", "Vrednost prodaje")}
            value={podaci.totals.salesValue.current}
            previous={podaci.totals.salesValue.previous}
            unit="RSD"
            locale={locale}
            tumaci={tumaciPeriod}
          />
          <KpiTile
            label={t("report.kpi.ruc", "Ostvareni RUC")}
            value={podaci.totals.ruc.current}
            previous={podaci.totals.ruc.previous}
            unit="RSD"
            locale={locale}
            tumaci={tumaciPeriod}
          />
          {/* Marža u dinarima po kilogramu stoji uz RUC u dinarima: ista zarada, druga mera.
              Brojka je ista mera koju nose Excel i PDF — ne izvodi se ponovo iz cena. */}
          {podaci.totals.rucPerKg.current !== null && (
            <KpiTile
              label={t("report.kpi.rucPerKg", "RUC po kilogramu")}
              value={podaci.totals.rucPerKg.current}
              previous={podaci.totals.rucPerKg.previous}
              unit="RSD/kg"
              locale={locale}
              note={t("report.kpi.rucPerKgNote", "samo nad kilogramskim stavkama")}
              tumaci={tumaciPeriod}
            />
          )}
        </div>
      )}

      <div role="tablist" aria-label={t("report.tabs", "Delovi izveštaja")} style={{ display: "flex", gap: 6 }}>
        {TABOVI.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? "btn" : "btn ghost"}
            onClick={() => setTab(id)}
          >
            {id === "products"
              ? t("report.tab.products", "Proizvodi")
              : id === "countries"
                ? t("report.tab.countries", "Zemlje")
                : id === "salespeople"
                  ? t("report.tab.salespeople", "Komercijalisti")
                  : id === "production"
                    ? t("report.tab.production", "Proizvodnja")
                    : t("report.tab.receivables", "Potraživanja")}
          </button>
        ))}
      </div>

      {!jePotrazivanja && !podaci && ucitava && <TableSkeleton rows={8} />}
      {jePotrazivanja && !potrazivanja && ucitavaP && <TableSkeleton rows={8} />}
      {jeProizvodnja && !proizvodnja && ucitavaR && <TableSkeleton rows={8} />}

      {podaci && tab === "products" && (
        <ProizvodiTab
          podaci={podaci}
          locale={locale}
          palette={palette}
          ucitava={ucitava}
          upit={upitIzvestaja}
        />
      )}
      {podaci && tab === "countries" && (
        <ZemljeTab
          podaci={podaci}
          locale={locale}
          palette={palette}
          ucitava={ucitava}
          upit={upitIzvestaja}
        />
      )}
      {podaci && tab === "salespeople" && (
        <KomercijalistiTab
          podaci={podaci}
          locale={locale}
          palette={palette}
          ucitava={ucitava}
          upit={upitIzvestaja}
        />
      )}
      {jeProizvodnja && proizvodnja && (
        <ProizvodnjaTab podaci={proizvodnja} locale={locale} palette={palette} ucitava={ucitavaR} />
      )}
      {jePotrazivanja && potrazivanja && (
        <>
          <PotrazivanjaTab
            podaci={potrazivanja}
            upit={upitIzvestaja}
            locale={locale}
            palette={palette}
            ucitava={ucitavaP}
          />
          <SpornoIBeleske locale={locale} canWrite={smeDaUpise} />
        </>
      )}
    </div>
  );
}



/**
 * Šta portal zapravo drži, u jednoj rečenici uz izveštaj.
 *
 * <p>Bez nje se brojka ne može odbraniti: kopija izvora seže do 22.09.2026, pa svaki period koji
 * je u toku izgleda manji nego što je bio, a za 2023. prethodne godine u ledgeru nema uopšte.
 * Pokrivenost `null` znači NEPOZNATO — tada se ne piše ništa, jer tvrdnja o potpunosti koju nam
 * niko nije dao nije naša da je damo.
 *
 * <p>Komponenta postoji zato što ovo nije svojina prodajnog taba: proizvodni nosi istu podelu
 * brašna sa istim dvema godinama, pa mu je i ograda ista.
 */
function Pokrivenost({
  coverage,
  prevFrom,
  prevTo,
  locale,
}: {
  coverage: Coverage | null;
  prevFrom: string | undefined;
  prevTo: string | undefined;
  locale: string;
}) {
  const { t } = useTranslation();
  if (!coverage) return null;
  const uredno = coverage.current === "FULL" && coverage.previous === "FULL";
  return (
    <div
      className="card"
      style={{
        padding: "10px 14px",
        fontSize: 13,
        color: "var(--ink-2)",
        borderLeft: `3px solid var(--${uredno ? "ink-3" : "warn"})`,
      }}
    >
      {t("report.coverage.until", {
        dan: formatDate(coverage.to, locale),
        defaultValue: "Podaci sežu do {{dan}}; to je dan do kog kopija izvora ide, ne dan zadnjeg dokumenta.",
      })}
      {coverage.current !== "FULL" && (
        <>
          {" "}
          {t(
            "report.coverage.partial",
            "Izabrani period prelazi taj dan, pa je nepun — rast ili pad se na njemu ne čita.",
          )}
        </>
      )}
      {coverage.previous === "NONE" && prevFrom && prevTo && (
        <>
          {" "}
          {t("report.coverage.noPrevious", {
            od: formatDate(prevFrom, locale),
            do: formatDate(prevTo, locale),
            defaultValue:
              "Prethodni period ({{od}} – {{do}}) nije uvezen, pa poređenja nema: kolona prošle godine je prazna, a ne nula.",
          })}
        </>
      )}
      {/* NEPUN PRETHODNI PERIOD SE IMENUJE, A NE SAMO ODSUTAN. Ograda je do 14.10.2026 pokrivala
          samo nepun TEKUĆI i odsutan prethodni, pa je period kom lane fali deo izgledao uredno:
          za 01.06.2023–31.05.2024 prethodni interval počinje pre uvoza, rast i pad su ispravno
          potisnuti, a nijedna rečenica nije rekla zašto. */}
      {coverage.previous === "PARTIAL" && prevFrom && prevTo && (
        <>
          {" "}
          {t("report.coverage.previousPartial", {
            od: formatDate(prevFrom, locale),
            do: formatDate(prevTo, locale),
            defaultValue:
              "Prethodni period ({{od}} – {{do}}) uvezen je samo delimično, pa poređenje sa njim nije puno: rast ili pad se ne čita.",
          })}
        </>
      )}
    </div>
  );
}

function ProizvodiTab({
  podaci,
  locale,
  palette,
  ucitava,
  upit,
}: {
  podaci: SalesReport;
  locale: string;
  palette: ReturnType<typeof useChartPalette>;
  ucitava: boolean;
  /** Isti upit kojim je tabela pročitana — spuštanje na artikal mora da pita za isti period. */
  upit: PeriodQuery;
}) {
  const { t } = useTranslation();
  // Zastavica se izvodi ovde, jer ovaj sastojak već nosi ceo izveštaj: nepotpun period ne dobija
  // tumačenje rasta ili pada, a oba broja ostaju na ekranu.
  const tumaciPeriod = pokrivenostPodnosiTumacenje(podaci.period.coverage);

  // Grafikon ide po NADGRUPI, jer je to nivo na kom se posao čita; grupe stoje u tabeli.

  const poNadgrupi = useMemo(() => {
    const m = new Map<string, { ime: string; ruc: number; rucPre: number | null }>();
    for (const r of podaci.groups) {
      const ime = r.supergroup || t("report.unclassified", "nerazvrstano");
      const p = m.get(ime) ?? { ime, ruc: 0, rucPre: 0 };
      p.ruc += r.ruc.current;
      // Prethodna godina koja nije uvezena je NEPOZNATA, pa i zbir nadgrupe postaje nepoznat —
      // nula bi na grafikonu izgledala kao da prošle godine nije bilo RUC-a.
      p.rucPre = p.rucPre === null || r.ruc.previous === null ? null : p.rucPre + r.ruc.previous;
      m.set(ime, p);
    }
    return [...m.values()].sort((a, b) => b.ruc - a.ruc);
  }, [podaci.groups, t]);

  // Marža po kilogramu: jedina veličina iz koje se vidi ZAŠTO se RUC pomerio. Grupe bez količine
  // se izostavljaju, jer cena po kilogramu bez kilograma nije cena.
  //
  // RUC po kilogramu NIJE razlika prodajne i nabavne cene. Ovde je do 13.10.2026 stajalo
  // `avgSalesPrice - avgPurchasePrice`, što za avgust 2026 daje 13,8969 rsd/kg, a mera je
  // 11,6955 — razlika su troškovi prodaje i fakturisani troškovi, koje RUC nosi a razlika cena ne.
  // Server meru računa na jednom mestu; ekran je čita, ne izvodi. Odatle i red poretka.
  const marza = useMemo(
    () =>
      podaci.groups
        .flatMap((r) =>
          r.quantity.current > 0 && r.group && r.rucPerKg.current !== null
            ? [
                {
                  ime: r.group,
                  prodajna: Number(r.avgSalesPrice.toFixed(2)),
                  nabavna: Number(r.avgPurchasePrice.toFixed(2)),
                  marza: Number(r.rucPerKg.current.toFixed(2)),
                },
              ]
            : [],
        )
        .sort((a, b) => b.marza - a.marza)
        .slice(0, 12),
    [podaci.groups],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, opacity: ucitava ? 0.55 : 1 }}>
      <section className="card" style={{ padding: 14 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>{t("report.chart.rucByGroup", "RUC po nadgrupama")}</h3>
        <div className="sub" style={{ marginBottom: 10 }}>
          {t(
            "report.chart.rucByGroupHint",
            "Ulje i sojina sačma se čitaju zajedno: minus sačme je druga strana plusa ulja iz iste ekstrakcije.",
          )}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={poNadgrupi} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="ime"
              tick={{ fontSize: 10, fill: palette.ink }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-18}
              textAnchor="end"
              height={64}
            />
            <YAxis tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} width={74} />
            <Tooltip
              contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
              labelStyle={{ color: palette.inkStrong }}
              formatter={(value, name) => [formatNumber(Number(value), locale), String(name)]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              dataKey="rucPre"
              name={t("report.chart.lastYear", "prošle godine")}
              fill={palette.inkMuted}
              radius={[3, 3, 0, 0]}
            />
            <Bar dataKey="ruc" name={t("report.chart.thisYear", "ovaj period")} radius={[3, 3, 0, 0]}>
              {/* Negativan RUC nije greška i ne sme da izgleda kao pozitivan. */}
              {poNadgrupi.map((p) => (
                <Cell key={p.ime} fill={p.ruc < 0 ? "var(--warn)" : palette.brand} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </section>

      {marza.length > 0 && (
        <section className="card" style={{ padding: 14 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15 }}>
            {t("report.chart.marginPerKg", "Cena i marža po kilogramu")}
          </h3>
          <div className="sub" style={{ marginBottom: 10 }}>
            {t("report.chart.marginPerKgHint", "Prodajna i nabavna cena jedne grupe; razlika je marža.")}
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={marza} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="ime"
                tick={{ fontSize: 10, fill: palette.ink }}
                axisLine={false}
                tickLine={false}
                interval={0}
                angle={-22}
                textAnchor="end"
                height={78}
              />
              <YAxis tick={{ fontSize: 11, fill: palette.ink }} axisLine={false} tickLine={false} width={62} />
              <Tooltip
                contentStyle={{ background: palette.surface, border: `1px solid ${palette.border}`, borderRadius: 8 }}
                labelStyle={{ color: palette.inkStrong }}
                formatter={(value, name) => [formatNumber(Number(value), locale), String(name)]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar
                dataKey="nabavna"
                name={t("report.chart.purchasePrice", "nabavna")}
                fill={palette.inkMuted}
                radius={[3, 3, 0, 0]}
              />
              <Line
                dataKey="prodajna"
                name={t("report.chart.salesPrice", "prodajna")}
                stroke={palette.brand}
                strokeWidth={2}
                dot={false}
              />
              {/* Mera se CRTA, ne samo poređa: razlika dve linije iznad nije ona, pa bez svoje
                  linije čitalac maržu odmerava oduzimanjem koje daje drugi broj. */}
              <Line
                dataKey="marza"
                name={t("report.chart.rucPerKg", "RUC po kg")}
                stroke="var(--ok)"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </section>
      )}

      <section className="card" style={{ padding: 0, overflowX: "auto" }}>
        <ProizvodiTabela
          redovi={podaci.groups}
          ukupno={podaci.totals}
          upit={upit}
          locale={locale}
          tumaci={tumaciPeriod}
        />
      </section>
    </div>
  );
}

